import { Button, useThemeMode } from "@meiden-connect/ui";
import { useQuery } from "@tanstack/react-query";
import { useCanvasApi, type RecentCanvasItem } from "../../lib/canvas-api";
import { generateCanvasId } from "../../lib/canvas-id";
import { normalizeCanvasTitle } from "../../lib/canvas-title";
import { formatJstDateTime } from "../../lib/datetime";
import { navigate } from "../../router";
import { RecentCanvasCard } from "./recent-canvas-card";

// ダッシュボード（TOP）画面。
// TOP のみライト/ダーク両対応とし、ヒーロー画像もテーマに合わせて切り替える。
export function DashboardView(): JSX.Element {
  const canvasApi = useCanvasApi();
  const themeMode = useThemeMode();
  const heroImageSrc = themeMode === "dark" ? "/ogp-dark.png" : "/ogp.png";

  const handleCreate = (): void => {
    const canvasId = generateCanvasId();
    navigate(`/c/${canvasId}`);
  };

  const recentCanvasesQuery = useQuery({
    queryKey: ["recent-canvases"],
    queryFn: canvasApi.fetchRecentCanvases,
    // 履歴の更新は他タブの操作でも起きるため、TOP に戻ったタイミングで再取得する。
    refetchOnMount: true,
  });
  const recentCanvases: readonly RecentCanvasItem[] = recentCanvasesQuery.data?.items ?? [];

  return (
    <div className="dashboard-content" data-testid="dashboard-root">
      <section className="dashboard-hero-card">
        <div className="dashboard-hero-copy">
          <p className="dashboard-eyebrow">MEIDEN CONNECT Collaborative Canvas</p>
          <h1>リファインメントキャンバスへようこそ</h1>
          <p>
            プロダクトバックログ・リファインメントを共同編集で進めるためのお絵かきツールです。
            新規キャンバスを作成するか、最近使ったキャンバスを開いてください。
          </p>
          <Button type="button" variant="action emphasis" onClick={handleCreate}>
            + 新規キャンバスを作成
          </Button>
        </div>
        <picture className="dashboard-hero-image-frame">
          <img alt="リファインメントキャンバスの紹介画像" src={heroImageSrc} />
        </picture>
      </section>

      <section className="dashboard-card">
        <h2>最近使ったキャンバス</h2>
        {recentCanvasesQuery.isLoading ? (
          <p className="dashboard-muted">読み込み中…</p>
        ) : recentCanvasesQuery.isError ? (
          <p className="dashboard-danger">履歴の取得に失敗しました。時間をおいて再読み込みしてください。</p>
        ) : recentCanvases.length === 0 ? (
          <p className="dashboard-muted">まだ最近使ったキャンバスはありません。</p>
        ) : (
          <div className="recent-canvas-grid">
            {recentCanvases.map((canvas) => (
              <RecentCanvasCard
                key={canvas.canvasId}
                canvasId={canvas.canvasId}
                title={normalizeCanvasTitle(canvas.title)}
                updatedAt={formatJstDateTime(canvas.lastOpenedAt)}
                thumbnailUrl={canvas.thumbnailUrl}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
